import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common'
import { FilterOption } from '../../models/tours-filter';
import { ToursFilterService } from '../../providers/tours-filter.service';
import { AutoSub, AutoSubOnDestroy } from '../../providers/auto-sub';
import { MotesPaths } from '../../models/motes-paths';
import { ProductVoucherList, UserService, VoucherService } from '../../providers';
import { MediaFile, ProductType, User } from '../../models';
import { of, switchMap } from 'rxjs';

export enum FiltersBarMode {
    TOUR_LIST = 'TOUR_LIST',
    TOUR_EXPLORER = 'TOUR_EXPLORER',
    STORY_EXPLORER = 'STORY_EXPLORER'
}

@AutoSubOnDestroy()
@Component({
    selector: 'filters-bar',
    templateUrl: './filters-bar.component.html',
    styleUrls: ['./filters-bar.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class FiltersBarComponent implements OnInit {

    @Input() set filtersBarMode(mode: FiltersBarMode) {
        this.updateMode(mode);
    }
    @Input() includeProfile: boolean = false;
    @Input() includeMapToggle: boolean = true;
    @Input() fullScreenMobile: boolean = false;
    @Input() showMyTours: boolean = true;
    @Input() showOldFilterLayout: boolean = false;

    @Output() profileClicked = new EventEmitter<void>();
    @Output() openMyToursClicked = new EventEmitter<void>();

    public mode: FiltersBarMode;
    public toggleText: string = `See map`;
    public toggleIcon: string = `map-fold`;
    public filtersOpen: boolean = false;
    public showFilters: boolean = false;
    public user: User;
    public profilePicture: MediaFile = null;

    // Vouchers my tours
    public  voucherCount: number = 0;
    public vouchersAreFetched: boolean = false;

    // Expose to view
    public FiltersBarMode = FiltersBarMode;
    public MotesPaths = MotesPaths;

    constructor(
        public toursFilterService: ToursFilterService,
        private router: Router,
        private route: ActivatedRoute,
        private location: Location,
        private voucherService: VoucherService,
        private userService: UserService) { }

    ngOnInit(): void {
        AutoSub(this).reg['profileImgSub'] = this.userService.watchCurrentUser().pipe(switchMap(user => {
            this.user = user;
            this.profilePicture = user?.profilePic?.thumb || null;
            if(!this.user || this.user.isAnonymous) return of([]);
            return this.voucherService.watchProductVoucherLists(this.user.key);
        })).subscribe((productVouchers: ProductVoucherList[]) => {
            this.voucherCount = productVouchers?.filter((productVoucherList) => !productVoucherList.product?.parent && productVoucherList.product?.type == ProductType.Tour).length || 0;
            this.vouchersAreFetched = true;
        });
    }

    private updateMode(mode: FiltersBarMode) {
        switch(mode) {
            case FiltersBarMode.TOUR_LIST:
                this.toggleText = $localize`See map`;
                this.toggleIcon = 'map-fold';
                this.showFilters = true;
                break;
            case FiltersBarMode.TOUR_EXPLORER:
                this.toggleText = $localize`See list`;
                this.toggleIcon = 'list';
                this.showFilters = true;
                break;
            case FiltersBarMode.STORY_EXPLORER:
                this.toggleText = $localize`View tour`;
                this.toggleIcon = 'route';
                this.showFilters = false;
                break;
            default: break;
        }
        this.mode = mode;
    }

    /**
     * On mode toggle clicked: Navigate based on current mode
     */
    public toggleView() {
        switch(this.mode) {
            case FiltersBarMode.TOUR_LIST:
                this.router.navigateByUrl(MotesPaths.TOUR_MAP);
                break;
            case FiltersBarMode.TOUR_EXPLORER:
                const targetUrl1 = '/';
                const targetUrl2 = '/tours'
                this.exitMap(targetUrl1, targetUrl2);
                break;
            case FiltersBarMode.STORY_EXPLORER:
                // Create the target URL by removing the 'map' param and keeping previewKey if any
                const urlTree = this.router.createUrlTree(['..'], { relativeTo: this.route, queryParamsHandling: "preserve" });
                const targetUrl = this.router.serializeUrl(urlTree);
                this.exitMap(targetUrl);
                break;
            default: break;
        }
    }

    private exitMap(targetUrl1: string, targetUrl2?: string) {
        // If the previous URL matches the target url go back in nav history, otherwise go forward
        const previousUrl = this.toursFilterService.getPreviousUrl();
        if(previousUrl == targetUrl1 || (targetUrl2 && (previousUrl == targetUrl2))) this.location.back();
        else this.router.navigateByUrl(targetUrl1);
    }

    /**
     * On filter option clicked: Update filtered tours
     */
    public toggleOption(optionClicked: FilterOption, options: FilterOption[], mutuallyExclusive: boolean) {
        // If options are mutually exclusive and one is chosen, clear remaining options
        if(mutuallyExclusive && !optionClicked.isChosen) options.map((option) => option.isChosen = false);
        optionClicked.isChosen = !optionClicked.isChosen;
        // Reset filtered tours and pipe them through filters
        this.toursFilterService.filterTours();
        this.scrollToTop();
    }

    public clearFilters() {
        if(this.toursFilterService.optionsChosen) this.scrollToTop();
        this.toursFilterService.clearFilters();
    }

    public clearSearch() {
        if(this.toursFilterService.searchInput) this.scrollToTop();
        this.toursFilterService.clearSearch();
    }

    public searchInputChanged() {
        this.toursFilterService.searchTours();
        this.scrollToTop();
    }

    private scrollToTop() {
        if(!window) return;
        window.scroll({ top: 0, left: 0, });
    }

    public openFilters(isFiltersOpen: boolean) {
        this.toursFilterService.filtersOpen = isFiltersOpen;
    }

    public openMyTours() {
        this.openMyToursClicked.emit();
    }
}
